import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { readSystemUsersAggregate } from '../utils/utils'
import { Col, Empty, Space, Table, message, theme, Tooltip, Button, Modal } from 'antd'
import { handleError } from '../../../lib/utils'
import coreReducer from '../../../../core/utils/reducer'
import CoreEnums from '../../../../core/utils/enums'
import AddPatient from './add'
import CustomRow from '../../../reusable-components/CustomRow'
import { deviceDetect } from 'react-device-detect'
import CustomButton from '../../../reusable-components/CustomButton'
import {
  faRefresh,
  faEye,
  faUserDoctor,
  faMessage,
  faFileAlt,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import dayjs from 'dayjs'
import { getBookingState, registerBooking } from '../../../Bookings/utils/bpm-utils'
import { readBookings } from '../../../Bookings/utils/utils'
import PatientExaminationForm from '../../../Bookings/components/ExaminationWrapper'
import TeamChatForm from '../../../TeamChat/components/team-chat-form'
import EditPatientProfile from './patient/EditPatient'
import systemUsersReducer from '../utils/reducer'
import store from '../../../../store'

const PatientDependants = ({ userRef, mainPatientInfo, mainMemeberDataIndex, parentTabKey }) => {
  const dispatch = useDispatch()
  const [receptionData, setReceptionData] = useState([])
  const [loading, setLoading] = useState(false)
  const [editTarget, setEditTarget] = useState(null)
  const [adding, setAdding] = useState(false)
  const [consultLoading, setConsultLoading] = useState(false)
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [teamChatProps, setTeamChatProps] = useState({ isOpen: false, userRef: null })
  const authState = useSelector((state) => state.auth)
  const { token } = theme.useToken()

  // Button styling to match FeatureButtonsRow
  const buttonStyle = {
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '0 16px',
    fontWeight: 500,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  }

  useEffect(() => {
    handleGetDependants()
    // eslint-disable-next-line
  }, [])

  // Add a new useEffect to re-run handleGetDependants when userRef changes
  useEffect(() => {
    const validPatientId = userRef || mainPatientInfo?._id
    if (validPatientId) {
      handleGetDependants()
    }
    // eslint-disable-next-line
  }, [userRef, mainPatientInfo])

  const handleGetDependants = async (dependants) => {
    let tmpDependants = []

    setLoading(true)

    try {
      // Only attempt to load dependants if we have a valid patient reference
      const validPatientId = userRef || mainPatientInfo?._id

      if (validPatientId) {
        tmpDependants = await readSystemUsersAggregate({ mainMemberId: validPatientId }, null, null, null, true)

        setReceptionData(tmpDependants)
      } else {
        // For new patients, initialize with empty array

        setReceptionData([])
      }
    } catch (e) {
      console.error('Error fetching dependants:', e)
      message.error(handleError(e, true))
    }

    setLoading(false)
  }

  // Add a proper resetFunction for the AddPatient component tab
  const handleResetTabFunction = () => {
    dispatch(
      coreReducer.actions.closeTab({
        targetKey: `${CoreEnums.tabKeys.DEPENDANTS}_new`,
        removeBreadcrumb: true
      })
    )
  }

  const handleOpenDependantForm = (record, isNew) => {
    if (!isNew) {
      // For existing dependants, use EditPatientProfile
      handleViewProfile(record)
    } else {
      // For new dependants, use AddPatient
      // Check if we have a valid patient reference to link dependants to
      const hasValidPatientRef = userRef || mainPatientInfo?._id

      if (!hasValidPatientRef) {
        message.info('Please save the main patient information first before adding dependants.')
        return
      }

      dispatch(
        coreReducer.actions.addTab({
          key: `${CoreEnums.tabKeys.DEPENDANTS}_new`,
          closable: true,
          label: 'New Dependant',
          children: (
            <AddPatient
              isDependant={true}
              mainPatientInfo={mainPatientInfo}
              refreshView={handleGetDependants}
              resetFunction={handleResetTabFunction}
              shouldStayOnPage={true}
            />
          )
        })
      )
    }
  }

  const handleViewProfile = (record) => {
    console.log('=== OPENING DEPENDANT PROFILE DEBUG ===')
    console.log('Dependant record:', record)
    console.log('Parent userRef:', userRef)
    console.log('Parent mainPatientInfo:', mainPatientInfo)
    console.log('Main patient ID from mainPatientInfo:', mainPatientInfo?._id)

    // Get current active records from Redux store
    const activeRecords = store.getState().systemUsers.activeRecords
    const tmpActiveRecords = [...activeRecords]

    // Check if record already exists in activeRecords
    const recordIndex = tmpActiveRecords.findIndex((item) => item._id === record._id)

    // If record doesn't exist, add it to activeRecords
    if (recordIndex === -1) {
      tmpActiveRecords.push(record)
    } else {
      // Update the record if it already exists
      tmpActiveRecords[recordIndex] = record
    }

    // Update activeRecords in Redux store
    dispatch(systemUsersReducer.actions.setActiveRecords(tmpActiveRecords))

    // Determine the correct root tab key based on user type
    const currentUserIsPatient = authState.agiliteUser?.extraData?.role?.type === 'patient'
    const rootTabKey = currentUserIsPatient ? CoreEnums.tabKeys.MY_PATIENT_FILE : CoreEnums.tabKeys.PATIENTS

    // Add tab with EditPatientProfile component
    dispatch(
      coreReducer.actions.addTab({
        key: `${CoreEnums.tabKeys.PATIENTS}_${record._id}`, // Always use PATIENTS for consistency
        closable: true,
        label: `Dependant: ${record.firstName} ${record.lastName}`,
        children: (
          <EditPatientProfile
            dataId={record._id}
            refreshView={() => handleGetDependants(mainPatientInfo?.dependants)}
            handleRootTabKey={() => rootTabKey}
            isDependant={true}
            parentId={mainPatientInfo?._id || userRef} // Use mainPatientInfo._id if available
            parentTabKey={parentTabKey} // Pass the parent tab key for direct navigation
          />
        )
      })
    )
  }

  const handleStartConsult = async (record) => {
    if (!record || !record._id) return

    try {
      // Show loading state
      setConsultLoading(true)

      // Get the patient's last appointment
      const patientAppointments = await readBookings({ userRef: record._id })

      if (!patientAppointments || patientAppointments.length === 0) {
        message.info('No appointments found for this patient')
        setConsultLoading(false)
        return
      }

      // Sort appointments by date (newest first)
      const sortedAppointments = patientAppointments.sort((a, b) => {
        const dateA = new Date(a.bookingDate)
        const dateB = new Date(b.bookingDate)
        return dateB - dateA
      })

      const lastAppointment = sortedAppointments[0]

      // Get workflow history if available
      let workflowHistory = {}
      if (lastAppointment.processRef) {
        workflowHistory = await getBookingState([lastAppointment.processRef])
        workflowHistory.history.splice(0, 1)
      }

      // Create a new data object that includes the patient record
      const appointmentData = {
        ...lastAppointment,
        patientRecord: {
          ...record,
          medicalAid: record.medicalAid || {} // Ensure medicalAid exists
        },
        workflowHistory: workflowHistory
      }

      // Add a tab for the examination
      dispatch(
        coreReducer.actions.addTab({
          key: `${CoreEnums.tabKeys.EXAMINATION}_${lastAppointment._id}`,
          closable: true,
          label: `Examination: ${record.firstName} ${record.lastName}`,
          children: <PatientExaminationForm data={appointmentData} />
        })
      )
    } catch (error) {
      message.error(handleError(error))
    } finally {
      setConsultLoading(false)
    }
  }

  const handleReset = () => {
    setAdding(false)
    setEditTarget(false)
  }

  // Define columns for the dependants table
  const columns = [
    {
      title: '',
      key: 'record',
      responsive: ['xs'],
      render: (text, record) => {
        return (
          <div style={deviceDetect().isMobile ? { textAlign: 'center' } : {}}>
            <b>
              {record.firstName} {record.lastName}
            </b>
            <br />
            <span>
              Date Of Birth: <b>{record.dateOfBirth ? dayjs(record.dateOfBirth).format('YYYY-MM-DD') : ''}</b>
            </span>
          </div>
        )
      }
    },
    { title: 'First Name', dataIndex: 'firstName', key: 'firstName', responsive: ['md'] },
    { title: 'Last Name', dataIndex: 'lastName', key: 'lastName', responsive: ['md'] },
    { title: 'ID / Passport Number', dataIndex: 'idNo', key: 'idNo', responsive: ['md'] },
    {
      title: 'Date Of Birth',
      dataIndex: 'dateOfBirth',
      key: 'dob',
      responsive: ['md'],
      render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : '')
    },
    {
      title: 'Actions',
      key: 'actions',
      responsive: ['md'],
      render: (text, record) => (
        <Space style={{ width: '100%', gap: 12 }}>
          {/* For patients, show a larger button; for professionals, show icon */}
          {authState.agiliteUser?.extraData?.role?.type === 'patient' ? (
            <Button
              type='primary'
              onClick={(e) => {
                e.stopPropagation()
                handleViewProfile(record)
              }}
              style={{
                borderRadius: '6px',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
              }}
            >
              <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: '6px' }} />
              View Record
            </Button>
          ) : (
            <Tooltip title='Patient Records'>
              <FontAwesomeIcon
                fontSize={20}
                style={{ color: '#ccc', transition: 'color 300ms ease' }}
                onClick={(e) => {
                  e.stopPropagation()
                  handleViewProfile(record)
                }}
                icon={faFileAlt}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = token.colorPrimary
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = '#ccc'
                }}
              />
            </Tooltip>
          )}

          {/* Team Chat - Only show for medical professionals and reception, not for patients */}
          {authState.agiliteUser?.extraData?.role?.type !== 'patient' && (
            <Tooltip title='Team Chat'>
              <FontAwesomeIcon
                fontSize={20}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = token.colorPrimary
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = '#ccc'
                }}
                style={{ color: '#ccc', transition: 'color 300ms ease' }}
                onClick={(e) => {
                  e.stopPropagation()
                  setTeamChatProps({ userRef: record._id, isOpen: true })
                }}
                icon={faMessage}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ]

  return (
    <>
      {adding || editTarget ? (
        <AddPatient
          isDependant
          mainPatientInfo={mainPatientInfo}
          resetFunction={handleReset}
          refreshView={handleGetDependants}
          shouldStayOnPage={true}
        />
      ) : (
        <CustomRow justify='center' style={deviceDetect().isMobile ? { textAlign: 'center' } : {}}>
          <Col span={24}>
            <CustomRow className='basic-card'>
              <Col span={24}>
                <Space wrap size={12}>
                  <Button
                    style={{
                      ...buttonStyle,
                      backgroundColor: '#1890ff',
                      borderColor: '#1890ff',
                      color: '#ffffff',
                      fontWeight: 500,
                      boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
                    }}
                    onClick={() => {
                      // Check if we have a valid patient reference to link dependants to
                      const hasValidPatientRef = userRef || mainPatientInfo?._id

                      if (hasValidPatientRef) {
                        setAdding(true)
                      } else {
                        // Show modal asking user to complete main patient details first
                        setShowCompletionModal(true)
                      }
                    }}
                    loading={loading}
                  >
                    Add Family Account
                  </Button>
                  {deviceDetect().isMobile ? undefined : (
                    <Tooltip title='Refresh'>
                      <Button
                        style={{
                          ...buttonStyle,
                          backgroundColor: '#1890ff',
                          borderColor: '#1890ff',
                          color: '#ffffff',
                          width: '40px', // Make it square
                          padding: '0', // Remove padding
                          fontWeight: 500,
                          boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
                        }}
                        icon={<FontAwesomeIcon icon={faRefresh} style={{ color: '#ffffff' }} />}
                        onClick={() => handleGetDependants()}
                      />
                    </Tooltip>
                  )}
                </Space>
              </Col>
              <Col span={24}>
                {receptionData && receptionData.length > 0 && (
                  <Table
                    showHeader={!deviceDetect().isMobile}
                    pagination={false}
                    columns={columns}
                    rowKey={(record) => record._id}
                    onRow={(record) => {
                      return {
                        onClick: () => {
                          handleOpenDependantForm(record, false)
                        }
                      }
                    }}
                    dataSource={receptionData}
                    loading={loading}
                  />
                )}
              </Col>
            </CustomRow>
          </Col>
        </CustomRow>
      )}

      {/* Completion Required Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#fff2e8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#fa8c16', fontSize: '20px' }} />
            </div>
            <span style={{ fontSize: '18px', fontWeight: '600', color: '#262626' }}>Complete Main Patient Details</span>
          </div>
        }
        open={showCompletionModal}
        onCancel={() => setShowCompletionModal(false)}
        footer={[
          <Button
            key='understood'
            type='primary'
            onClick={() => setShowCompletionModal(false)}
            style={{
              backgroundColor: token.colorPrimary,
              borderColor: token.colorPrimary,
              height: '40px',
              fontWeight: '500',
              borderRadius: '6px'
            }}
          >
            I Understand
          </Button>
        ]}
        centered
        width={480}
        style={{
          borderRadius: '12px'
        }}
        bodyStyle={{
          padding: '24px 32px',
          textAlign: 'center'
        }}
      >
        <div style={{ marginTop: '16px' }}>
          <p
            style={{
              fontSize: '16px',
              color: '#595959',
              marginBottom: '16px',
              lineHeight: '1.6'
            }}
          >
            Please complete the main patient's details in the <strong>"Patient Details"</strong> tab before adding
            dependants.
          </p>
          <p
            style={{
              fontSize: '14px',
              color: '#8c8c8c',
              margin: 0,
              lineHeight: '1.5'
            }}
          >
            All dependants will be linked to the main patient account, so the main patient information must be saved
            first.
          </p>
        </div>
      </Modal>

      {/* Team Chat Modal */}
      {teamChatProps.isOpen && (
        <TeamChatForm
          isOpen={teamChatProps.isOpen}
          userRef={teamChatProps.userRef}
          onClose={() => setTeamChatProps({ isOpen: false, userRef: null })}
        />
      )}
    </>
  )
}

export default PatientDependants
